// File path : src\hooks\useImageViewer.ts

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useLazyGetTextForROIQuery } from 'api/document';

// Import the new API hook
import {
  useGetFileCommentsQuery,
  useGetFileHeaderDataQuery,
  useGetImageViewerDocumentByteQuery,
  useGetQaFileTablesQuery,
  useLazyGetImageViewerPdfFileQuery,
  useLazyGetQaTableFieldsQuery,
  useLazyGetSearchTextInFileQuery,
  useUpdateFileHeaderDataMutation,
  useUpdateFileStatusMutation,
  useUpdateQaTableCellMutation
} from 'api/imageViewer';
import {
  CavseRefType,
  FileHeaderData,
  FileStatus,
  GetFileHeaderDataResponse,
  GetQaTablesFieldsListResponse,
  ImageViewerGetDocumentBytesResponse,
  ImageViwerNavigateFile,
  TechniqueNameListTypes,
  UpdateQcTableCellPayload
} from 'types/image-viewer';
import { base64ToBlob } from 'utils/fileUtils';
import { useDispatch } from 'react-redux';
import { openDefaultErrorSnackbar, openDefaultSuccessSnackbar } from 'store/reducers/snackbar';
import { find, findIndex } from 'lodash';
import { generateMetricsFromROI } from 'utils/imageViewerUtils';
import { useGetPredictionFileListQuery } from 'api/prediction';
import {
  AddTablePatternPayload,
  VerifcaitonNewPatternPayloadType,
  VerificationTechniquesEnum,
  VerificationTechniquesLabelEnum
} from 'types/verification';
import { useAddTablePatternMutation, useVerificationAddNewPatternMutation } from 'api/verification';
import { BathcFileDetailsTypes, ReviewDocumentStatusType } from 'types/prediction';
import {
  clearColumnName,
  clearCurrentFieldName,
  clearRoiName,
  clearsetHeaderPatternTextRoi,
  clearsetPatternTextRoi
} from 'store/reducers/columnSlice';
import { setAddPatternRubberBand } from 'store/reducers/taggingSlice';

export const useImageViewer = () => {
  const canvasFunctionRef = useRef<CavseRefType>(null);
  const isTableCellActiveRef = useRef<boolean>(false);
  const { batchId, fileId } = useParams();
  const navigate = useNavigate(); // For updating the URL
  const dispatch = useDispatch(); // For updating the URL
  const location = useLocation();
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [isRighpanelImageLoading, setIsRighpanelImageLoading] = useState<boolean>(false);
  const [currentPageDetails, setCurrentPageDetails] = useState<ImageViewerGetDocumentBytesResponse>();
  const [selectedField, setSelectedFieldState] = useState<GetFileHeaderDataResponse['headerData'][0] | null>(null);
  const [selectedTableCell, setSelectedTableCell] = useState<UpdateQcTableCellPayload | null>(null);
  const [fileList, setFileList] = useState<BathcFileDetailsTypes[]>([]);
  const [columnName, setColumnName] = useState<any>('');
  // const roiName = useSelector((state: RootState) => state.columnSlice.roiName);
  const [fileLevelDetails, setFileLevelDetails] = useState<GetFileHeaderDataResponse['filedetail']>(
    {} as GetFileHeaderDataResponse['filedetail']
  );
  const [fileHeaderData, setFileHeaderData] = useState<GetFileHeaderDataResponse['headerData']>([]);
  const [selectedAddPattern, setSelectedAddPattern] = useState<TechniqueNameListTypes | null>(null);

  const [triggerGetTextForROI] = useLazyGetTextForROIQuery(); // Lazy query for flexibility

  const { data: documentPageDetailsRes, isFetching: isLoadingImageBytes } = useGetImageViewerDocumentByteQuery(
    { fileID: Number(fileId), pageNo: currentPageNumber, batchID: Number(batchId) },
    { skip: !fileId || !batchId }
  );

  const {
    data: fileListResponse,
    refetch: refetchFileList,
    isFetching: isFileListFetching
  } = useGetPredictionFileListQuery({ batchID: Number(batchId || 0), shouldRemoveVerified: false }, { skip: !batchId });
  const { data: qaTables } = useGetQaFileTablesQuery({ fileId: Number(fileId || 0) }, { skip: !fileId });
  // const { data: fileComments } = useGetFileCommentsQuery({ fileID: Number(fileId) }, { skip: !fileId });

  const { data: fileComments } = useGetFileCommentsQuery(
    { fileID: Number(fileId) },
    {
      skip: !fileId,
      selectFromResult: ({ data, ...rest }) => ({
        data: typeof data === 'string' && data === 'No comments available.' ? [] : data,
        ...rest
      })
    }
  );

  const [fetchFilePdf] = useLazyGetImageViewerPdfFileQuery();
  const [searchTextInFileQuery] = useLazyGetSearchTextInFileQuery();

  const {
    data: fileHeaderDataResponse,
    isLoading: isFileHeaderDataLoading,
    isFetching: isFileHeaderDataFetching
  } = useGetFileHeaderDataQuery({ fileId: Number(fileId), batchID: Number(batchId) }, { skip: !fileId || !batchId });

  const [updateFieldDataAPI] = useUpdateFileHeaderDataMutation();
  const [updateFileStatus, { isLoading: isFileStatusUpdating }] = useUpdateFileStatusMutation();
  const [updateTableCell] = useUpdateQaTableCellMutation();
  const [addNewPatternMutation] = useVerificationAddNewPatternMutation();
  const [addTablePatternMutation] = useAddTablePatternMutation();

  const shouldRestrictModification: boolean = useMemo(() => {
    const batchDetails = find(fileListResponse || [], { fileID: Number(fileId) });
    if (!batchDetails?.statusName) return false;
    return [ReviewDocumentStatusType.QCVERIFIED, ReviewDocumentStatusType.EXPORTED].includes(
      batchDetails.statusName as ReviewDocumentStatusType
    );
  }, [fileId, fileListResponse]);

  useEffect(() => {
    if (fileListResponse) {
      setFileList(
        fileListResponse.filter(
          ({ statusName }) => ![ReviewDocumentStatusType.QCVERIFIED, ReviewDocumentStatusType.EXCEPTION].includes(statusName as any)
        )
      );
    } else {
      setFileList([]);
    }
  }, [fileListResponse]);

  useEffect(() => {
    if (fileHeaderDataResponse) {
      setFileLevelDetails(fileHeaderDataResponse.filedetail);
      setFileHeaderData(fileHeaderDataResponse.headerData);
    }
  }, [fileHeaderDataResponse]);

  // Set current page details when document page details response is available
  useEffect(() => {
    if (documentPageDetailsRes) {
      setCurrentPageDetails({ ...documentPageDetailsRes });
    }
  }, [documentPageDetailsRes]);

  // Navigate to back if selected file Id does not exists in file list
  useEffect(() => {
    if (fileListResponse && fileListResponse.length > 0 && fileId && !fileListResponse.find(({ fileID }) => fileID === Number(fileId))) {
      navigate(location.state?.parentPath || '/', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileListResponse, fileId]);

  const onViewPdfClick = async () => {
    try {
      const { data } = await fetchFilePdf({ fileId: fileId ? Number(fileId) : 0 });

      if (data) {
        const blob = base64ToBlob(data.split(',')[1], 'application/pdf');
        const url = URL.createObjectURL(blob);
        window.open(
          url,
          'CMR_Document_Image_Viewer_File_PDF',
          `toolbar=yes,resizable=yes,height=${window.screen.height},width=${window.screen.width},scrollbars=yes,status=yes`
        );
      }
    } catch (e) {
      console.error(e);
    }
  };

  const [fetchTableFields] = useLazyGetQaTableFieldsQuery();

  const searchTextInFile = (searchText: string): Promise<any[]> =>
    new Promise(async (resolve, reject) => {
      try {
        const { data }: { data?: unknown } = await searchTextInFileQuery({ fileID: Number(fileId || 0), searchText });
        if (data) {
          resolve(data as any[]);
        } else {
          resolve([]);
        }
      } catch (e) {
        reject(e);
      }
    });

  const updateFieldROI = useCallback(
    async (roi: string, canvasPageNo?: number) => {
      try {
        if (!selectedTableCell && !selectedField) {
          dispatch(
            openDefaultErrorSnackbar({
              open: true,
              message: 'Please select a field first then try again!',
              variant: 'alert',
              alert: { color: 'error' },
              close: true
            })
          );
          return;
        }

        const coordinates = generateMetricsFromROI(roi);

        if (isTableCellActiveRef.current && selectedTableCell) {
          const pageNumber = canvasPageNo || selectedTableCell.pageNumber;
          const { data: roiData }: any = await triggerGetTextForROI({
            FileId: selectedTableCell?.fileID,
            PageNo: pageNumber,
            Xaxis: coordinates?.left,
            Yaxis: coordinates?.top,
            Width: coordinates?.width,
            Height: coordinates?.height
          }).unwrap();
          await updateTableCell({
            resultID: selectedTableCell.resultID,
            pageNumber: pageNumber,
            updateValue: roiData?.text || '',
            ROI: roi
          });
          await fetchTableFields({
            fieldId: selectedTableCell.fieldId || 0,
            fileId: selectedTableCell.fileID || 0
          });
        } else {
          if (!selectedField) return;
          const pageNumber = canvasPageNo || selectedField.pageNumber;
          const { data: roiData }: any = await triggerGetTextForROI({
            FileId: selectedField.fileID,
            PageNo: pageNumber,
            Xaxis: coordinates?.left,
            Yaxis: coordinates?.top,
            Width: coordinates?.width,
            Height: coordinates?.height
          }).unwrap();

          await updateFieldData({
            isHeader: !selectedField.childID,
            resultID: selectedField.resultID,
            fileID: selectedField.fileID,
            fieldID: selectedField.fieldID,
            currentValue: selectedField.fieldValue,
            updateValue: roiData?.text || '',
            updateValuePageNo: pageNumber,
            confidenceFlag: selectedField.confidenceFlag,
            updateValueROI: roi,
            originalROI: selectedField.extractedROI,
            childID: selectedField.childID,
            batchID: Number(batchId || 0)
          });
        }

        dispatch(
          openDefaultSuccessSnackbar({
            open: true,
            message: 'Successfully updated new ROI for selected field.',
            variant: 'alert',
            alert: { color: 'success' },
            close: true
          })
        );
      } catch (e) {}
      // eslint-disable-next-line prettier/prettier, react-hooks/exhaustive-deps
    },
    [batchId, selectedField, selectedTableCell]
  );

  const handleFileNavigation: ImageViwerNavigateFile = useCallback(
    (direction) => async (isCompletedQc?: boolean) => {
      try {
        if (!fileList || !fileLevelDetails) return;

        const currentFileIndex = findIndex(fileList, { fileID: fileLevelDetails.fileID });
        const nextBatchDetails = fileList[currentFileIndex + (direction === 'NEXT' ? 1 : -1)];
        if (nextBatchDetails) {
          await refetchFileList().unwrap();
          setCurrentPageNumber(1);
          navigate(`/review/image-viewer/${batchId}/${nextBatchDetails.fileID}`, { replace: true, state: location.state });
        } else {
          navigate(location.state?.parentPath || '/', {
            replace: true,
            state: location?.state?.parentState || {
              batchId: batchId,
              docTypeID: fileLevelDetails.docTypeID,
              processID: fileLevelDetails.processID
            }
          });
        }
        if (!isCompletedQc) {
          await updateFileStatus({
            fileID: Number(fileId || 0),
            batchID: Number(batchId || 0),
            status: FileStatus.QC_INCOMPLETE,
            accuracy: String(fileLevelDetails?.accuracy || 0)
          }).unwrap();
        }
      } catch (e) {
        console.error(e);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fileList, batchId, fileLevelDetails, location.state, fileId]
  );

  const handleCompleteQcMoveNext = () =>
    new Promise<boolean>(async (resolve, reject) => {
      try {
        await updateFileStatus({
          fileID: Number(fileId || 0),
          batchID: Number(batchId || 0),
          status: FileStatus.QC_VERIFIED,
          accuracy: String(fileLevelDetails?.accuracy || 0)
        }).unwrap();
        dispatch(
          openDefaultSuccessSnackbar({
            open: true,
            message: `Quality check completed for '${fileLevelDetails?.fileName}' file successfully`,
            variant: 'alert',
            alert: { color: 'success' },
            close: true
          })
        );
        handleFileNavigation('NEXT')(true);
        resolve(true);
      } catch (e) {
        reject(false);
      }
    });

  const onSelectTabcelCell = (cellData: UpdateQcTableCellPayload) => {
    setSelectedTableCell(cellData);
    setSelectedFieldState(null);
    isTableCellActiveRef.current = true;
    if (canvasFunctionRef.current?.highlightRubberBand) {
      if (cellData?.updateValue) {
        if (cellData?.pageNumber) {
          setCurrentPageNumber(cellData.pageNumber);
        }
        canvasFunctionRef.current.highlightRubberBand(
          cellData ? ({ ...generateMetricsFromROI(cellData.ROI), pageNo: cellData.pageNumber } as any) : null
        );
      } else {
        canvasFunctionRef.current.highlightRubberBand(null);
      }
    }
  };

  const setSelectedField = (fieldData: FileHeaderData | null) => {
    dispatch(clearColumnName());
    dispatch(clearRoiName());
    dispatch(clearCurrentFieldName());
    dispatch(clearsetPatternTextRoi());
    dispatch(clearsetHeaderPatternTextRoi());
    setSelectedFieldState(fieldData);
    setSelectedTableCell(null);
    isTableCellActiveRef.current = true;
    if (canvasFunctionRef.current?.highlightRubberBand) {
      if (fieldData?.fieldValue) {
        if (fieldData?.pageNumber) {
          setCurrentPageNumber(fieldData.pageNumber);
        }
        canvasFunctionRef.current.highlightRubberBand(
          fieldData ? ({ ...generateMetricsFromROI(fieldData.roi), pageNo: fieldData.pageNumber } as any) : null
        );
      } else {
        canvasFunctionRef.current.highlightRubberBand(null);
      }
    }
  };

  const updateFieldData: any = (payload: any, skipRefetch: boolean = false) =>
    new Promise(async (resolve, reject) => {
      try {
        const data = await updateFieldDataAPI(payload).unwrap();
        setFileLevelDetails(data.filedetail);
        //if (!skipRefetch) {
        setFileHeaderData((pre) => {
          let prevHeaderData = [...pre];
          const targetHeaderDataIndex = findIndex(prevHeaderData, { fieldID: data.headerData[0].fieldID });
          const selectedFieldIndex = findIndex(prevHeaderData, { fieldID: selectedField?.fieldID });
          const isSelectedFieldModified = targetHeaderDataIndex === selectedFieldIndex;
          if (targetHeaderDataIndex >= 0) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { techniqueNameList, ...updateFieldData } = data.headerData[0];
            prevHeaderData[targetHeaderDataIndex] = { ...prevHeaderData[targetHeaderDataIndex], ...updateFieldData };
            if (selectedField?.childID && !payload.isSwap) {
              const childData = updateFieldData.childData?.find(({ childID }) => childID === selectedField?.childID);
              if (childData) {
                if (isSelectedFieldModified)
                  setSelectedField({
                    ...prevHeaderData[targetHeaderDataIndex],
                    ...childData,
                    roi: childData.extractedROI,
                    childID: childData.childID
                  });
              } else {
                if (isSelectedFieldModified) setSelectedField(null);
              }
            } else {
              if (isSelectedFieldModified) setSelectedField(prevHeaderData[targetHeaderDataIndex]);
            }
          }
          return prevHeaderData;
        });
        //}

        resolve(true);
      } catch (e) {
        reject(e);
      }
    });

  const onClickAddPattern = (patternType: TechniqueNameListTypes, fieldDetails: GetFileHeaderDataResponse['headerData'][0]) => () => {
    dispatch(clearColumnName());
    dispatch(clearRoiName());
    dispatch(clearCurrentFieldName());
    dispatch(clearsetPatternTextRoi());
    dispatch(clearsetHeaderPatternTextRoi());
    dispatch(setAddPatternRubberBand(true));
    setSelectedFieldState(fieldDetails);
    setSelectedAddPattern(patternType);
  };

  const handleAddNewPattern = (data: any) =>
    new Promise(async (resolve, reject) => {
      try {
        if (!selectedAddPattern) return;
        let successMessage = '';
        if (String(selectedAddPattern.techniqueType).trim() === VerificationTechniquesEnum.TABLE && selectedTableCell) {
          const payload: AddTablePatternPayload = {
            taxonomyID: 0,
            columnID: selectedTableCell.originalColumnID || 0,
            patternValue: data.documentPattern,
            patternROI: data.patternROI,
            patternPageNo: selectedTableCell.pageNumber || 0,
            fileId: selectedTableCell.fileID || 0,
            fieldId: selectedAddPattern.fieldID,
            techId: selectedTableCell?.techID || 0
          };
          successMessage = `Pattern added to '${selectedTableCell.columnName}' column successfully`;
          await addTablePatternMutation(payload).unwrap();
        } else {
          const formatWord = (word: string | undefined | null): string => {
            if (!word) return '';
            return word.replace(/\s+/g, '').toUpperCase();
          };
          const formattedWord = formatWord(selectedAddPattern.patternLabel);

          const firstPart = selectedAddPattern.techniqueName
            ? selectedAddPattern.techniqueName.includes('Nested Lookup Printed')
              ? 'NestedLookupPrinted'
              : selectedAddPattern.techniqueName.includes('Nested - Keyvaluepair printed')
              ? 'NestedKeyvaluepairprinted'
              : selectedAddPattern.techniqueName.toLowerCase().includes('lookup printed')
              ? 'Lookup'
              : selectedAddPattern.techniqueName.toUpperCase().includes('KEYVALUEPAIR')
              ? 'KeyValuePair'
              : selectedAddPattern.techniqueName.split('-')[0]?.trim()
            : '';

          let payload: VerifcaitonNewPatternPayloadType = {
            fileId: Number(fileId),
            fieldId: selectedAddPattern.fieldID,
            techId: selectedAddPattern.techniqueID,
            taxonomyID: 0,
            isKeyValuePair:
              firstPart === 'NestedKeyvaluepairprinted'
                ? true
                : formattedWord === 'ADDCONTEXT'
                ? false
                : firstPart === VerificationTechniquesLabelEnum[VerificationTechniquesEnum.KEY_VALUE_PAIR],
            patternValue:
              formattedWord === 'ADDCONTEXT' ? data.patternText : data.sectionHeader ? data.sectionHeader : data.patternText || '',
            IsContextCloud: formattedWord === 'ADDCONTEXT' ? true : false,
            patternROI: data.patternROI,
            patternPageNo: currentPageNumber,
            contentDirectionID: 5,
            sectionHeader: data.sectionHeader,
            sectionFooter: data.sectionFooter,
            isExpression: Boolean(data.expressionPattern),
            expressionPattern: data.expressionPattern || '',
            expressionRegex: data.expressionRegex || '',
            isLookup:
              firstPart === 'NestedLookupPrinted'
                ? true
                : Boolean(data.documentPattern && selectedAddPattern.patternLabel !== VerificationTechniquesEnum.TABLE),
            documentPattern:
              firstPart === 'NestedLookupPrinted'
                ? data.sectionHeader
                  ? data.sectionHeader
                  : data.documentPattern
                : data.documentPattern || '',
            displayValue: data.displayValue
              ? data.displayValue
              : firstPart === 'NestedLookupPrinted'
              ? data.sectionHeader
                ? data.sectionHeader
                : data.documentPattern
              : data.documentPattern
          };
          successMessage = `Pattern added to '${selectedField?.fieldName}' field successfully`;
          await addNewPatternMutation(payload).unwrap();
        }

        dispatch(
          openDefaultSuccessSnackbar({
            open: true,
            message: successMessage,
            variant: 'alert',
            alert: { color: 'success' },
            close: true
          })
        );
        setSelectedAddPattern(null);
        // setSelectedFieldState(null);
        // setSelectedTableCell(null);
        resolve(true);
      } catch (e) {
        reject(e);
      }
    });

  const handleTableAddPattern = (cellData: GetQaTablesFieldsListResponse) => {
    setSelectedTableCell({
      resultID: cellData.resultID,
      updateValue: cellData.extractedResult,
      ROI: cellData.roi,
      fileID: cellData.fileID,
      pageNumber: cellData.pageNumber,
      fieldId: cellData.fieldID,
      columnID: cellData.columnID,
      columnName: cellData.columnName,
      techID: cellData.techID || 0,
      originalColumnID: cellData.originalColumnID
    });
    setSelectedFieldState(null);
    setSelectedAddPattern({
      fieldID: cellData.fieldID,
      techniqueID: 0,
      techniqueName: VerificationTechniquesLabelEnum[VerificationTechniquesEnum.TABLE],
      techniqueType: VerificationTechniquesEnum.TABLE
    });
  };

  const clearSelectedAddPattern = () => {
    setSelectedAddPattern(null);
  };

  return {
    fileId,
    currentPageDetails,
    currentPageNumber,
    setCurrentPageNumber,
    setIsRighpanelImageLoading,
    isRighpanelImageLoading,
    isLoadingImageBytes,
    fileLevelDetails,
    onViewPdfClick,
    searchTextInFile,
    fileComments,
    fileHeaderData,
    isFileHeaderDataLoading,
    selectedField,
    setSelectedField,
    updateFieldData,
    updateFieldROI,
    handleFileNavigation,
    handleCompleteQcMoveNext,
    qaTables: qaTables || [],
    canvasFunctionRef,
    onSelectTabcelCell,
    selectedTableCell,
    onClickAddPattern,
    selectedAddPattern,
    handleAddNewPattern,
    handleTableAddPattern,
    shouldRestrictModification,
    clearSelectedAddPattern,
    columnName,
    setColumnName,
    isCompleteQcLoading: isFileHeaderDataFetching || isFileListFetching || isLoadingImageBytes || isFileStatusUpdating
  };
};
